/**
 * Main HexMap component with pan/zoom controls.
 * Supports both desktop (mouse) and mobile (touch) interactions.
 */

import { useRef, useState, useEffect, useCallback } from 'react';
import type { HexMapProps, HexData } from './types';
import { type AxialCoord, hexKey } from '@/lib/hexMath';
import { HexMapCanvas } from './HexMapCanvas';
import { HexTooltip } from './HexTooltip';
import { useHexMap } from './useHexMap';
import { StarfieldBackground } from '@/components/StarfieldBackground';

// State for the hover tooltip
interface TooltipState {
    hex: HexData | null;
    coord: AxialCoord;
    position: { x: number; y: number };
    visible: boolean;
}

// State for mobile tap tracking (two-tap interaction)
interface TapState {
    coord: AxialCoord | null;
    timestamp: number;
}

// Timeout for resetting tap state (ms) - if user doesn't tap same hex within this time, reset
const TAP_TIMEOUT = 2000;

export function HexMap({ hexes, onHexClick, onHexHover, recentlyUpdatedIds, claimableCoords, canClaimHexes = false, allUnclaimedClaimable = false, backgroundTheme = 'current', className = '' }: HexMapProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
    const [tooltip, setTooltip] = useState<TooltipState>({
        hex: null,
        coord: { q: 0, r: 0 },
        position: { x: 0, y: 0 },
        visible: false,
    });

    // Track last tap for mobile two-tap interaction
    const [lastTap, setLastTap] = useState<TapState>({ coord: null, timestamp: 0 });

    const { viewport, handlers, controls, getHexAt } = useHexMap(hexes);

    // Handle hover events from the canvas (desktop only)
    const handleHexHover = useCallback(
        (hex: HexData | null, coord: AxialCoord | null, mousePos: { x: number; y: number } | null) => {
            if (coord && mousePos) {
                setTooltip({
                    hex,
                    coord,
                    position: mousePos,
                    visible: true,
                });
            } else {
                setTooltip((prev) => ({ ...prev, visible: false }));
            }

            // Also call the external hover handler if provided
            if (onHexHover) {
                onHexHover(hex, coord);
            }
        },
        [onHexHover]
    );

    // Handle mobile tap - implements two-tap interaction pattern
    // First tap: show tooltip, Second tap on same hex: navigate
    const handleMobileTap = useCallback(
        (hex: HexData | null, coord: AxialCoord, screenPos: { x: number; y: number }) => {
            const now = Date.now();
            const isSameHex = lastTap.coord && lastTap.coord.q === coord.q && lastTap.coord.r === coord.r;
            const isWithinTimeout = now - lastTap.timestamp < TAP_TIMEOUT;

            if (isSameHex && isWithinTimeout) {
                // Second tap on same hex - navigate (call onHexClick)
                setTooltip((prev) => ({ ...prev, visible: false }));
                setLastTap({ coord: null, timestamp: 0 });
                if (onHexClick) {
                    onHexClick(hex, coord);
                }
            } else {
                // First tap or different hex - show tooltip
                setTooltip({
                    hex,
                    coord,
                    position: screenPos,
                    visible: true,
                });
                setLastTap({ coord, timestamp: now });
            }
        },
        [lastTap, onHexClick]
    );

    // Clear tap state when tapping outside or after timeout
    useEffect(() => {
        if (lastTap.coord) {
            const timer = setTimeout(() => {
                setLastTap({ coord: null, timestamp: 0 });
            }, TAP_TIMEOUT);
            return () => clearTimeout(timer);
        }
    }, [lastTap]);

    // Track container size
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const updateSize = () => {
            setDimensions({
                width: container.clientWidth,
                height: container.clientHeight,
            });
        };

        updateSize();

        const observer = new ResizeObserver(updateSize);
        observer.observe(container);

        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={containerRef}
            className={`relative overflow-hidden ${className}`}
            {...handlers}
        >
            {/* Background themes */}
            {backgroundTheme === 'parchment' && (
                <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                        background:
                            'linear-gradient(135deg, #f5e6d3 0%, #e8d5b7 25%, #f0e2cc 50%, #e5d4b8 75%, #f2e4c9 100%)',
                        backgroundBlendMode: 'multiply',
                        filter: 'brightness(0.95) contrast(0.98)',
                    }}
                />
            )}
            {backgroundTheme === 'starfield' && (
                <StarfieldBackground className="absolute inset-0" />
            )}
            <HexMapCanvas
                hexes={hexes}
                viewport={viewport}
                width={dimensions.width}
                height={dimensions.height}
                onHexClick={onHexClick}
                onHexHover={handleHexHover}
                onMobileTap={handleMobileTap}
                getHexAt={getHexAt}
                recentlyUpdatedIds={recentlyUpdatedIds}
                claimableCoords={claimableCoords}
                allUnclaimedClaimable={allUnclaimedClaimable}
                backgroundTheme={backgroundTheme}
            />

            {/* Hover tooltip */}
            <HexTooltip
                hex={tooltip.hex}
                coord={tooltip.coord}
                position={tooltip.position}
                visible={tooltip.visible}
                isClaimable={(claimableCoords?.has(hexKey(tooltip.coord)) ?? false) || allUnclaimedClaimable}
                canClaimHexes={canClaimHexes}
            />

            {/* Zoom controls */}
            <div className="absolute bottom-4 right-4 z-20 flex flex-col gap-2">
                <button
                    onClick={controls.zoomIn}
                    className="w-10 h-10 bg-slate-800 hover:bg-slate-700 text-white rounded-lg flex items-center justify-center border border-slate-600"
                    title="Zoom in"
                >
                    +
                </button>
                <button
                    onClick={controls.zoomOut}
                    className="w-10 h-10 bg-slate-800 hover:bg-slate-700 text-white rounded-lg flex items-center justify-center border border-slate-600"
                    title="Zoom out"
                >
                    −
                </button>
                <button
                    onClick={controls.resetView}
                    className="w-10 h-10 bg-slate-800 hover:bg-slate-700 text-white rounded-lg flex items-center justify-center border border-slate-600"
                    title="Reset view"
                >
                    ⌂
                </button>
            </div>

            {/* Zoom level indicator */}
            <div className="absolute bottom-4 left-4 z-20 bg-slate-800/80 text-white text-sm px-3 py-1 rounded border border-slate-600">
                {Math.round(viewport.zoom * 100)}%
            </div>
        </div>
    );
}

export default HexMap;
